import { makeStyles, mergeClasses, OptionOnSelectData, SelectionEvents } from '@fluentui/react-components';
import React from 'react';
import { Field } from 'react-final-form';
import { useStackWithFullWidthItemStyles } from '../../../../themes/styles/flexbox-styles';
import { FieldError } from '../../../common/form/form-field-error';
import { AddOrUpdateExpirationFormDateControlContainer } from './add-or-update-expiration-form-date-control';
import AddOrUpdateExpirationFormEnableControl from './add-or-update-expiration-form-enable-control';
import { AddOrUpdateExpirationFormTimeControlContainer } from './add-or-update-expiration-form-time-control';
import AddOrUpdateExpirationFormTimeZoneControl from './add-or-update-expiration-form-timezone-control';
import { AddOrUpdateExpirationFormFieldName, EnvironmentExpirationViewModel } from './models';
import { areExpirationDatesEqual } from './selectors';

interface AddOrUpdateExpirationFormFieldGroupProps {
    isSubmitting: boolean;
    enableExpiration: boolean;
}

/**
 * Styles
 */

const useFieldGroupStyles = makeStyles({
    root: {
        gap: '8px',
    },
});

const useTimeFieldStyles = makeStyles({
    root: {
        gap: '16px',
    },
});

const useCheckboxStyles = makeStyles({
    root: {
        //needed to align the checkbox with the dropdowns
        marginLeft: '-8px',
        marginBottom: '8px',
    },
});
/**
 * END Styles
 */

export const AddOrUpdateExpirationFormFieldGroup: React.FC<AddOrUpdateExpirationFormFieldGroupProps> = (props) => {
    const { isSubmitting, enableExpiration } = props;

    // Style hooks
    const fieldGroupStyles = useFieldGroupStyles();
    const timeFieldStyles = useTimeFieldStyles();
    const stackStyles = useStackWithFullWidthItemStyles();
    const checkboxStyles = useCheckboxStyles();

    return (
        <div className={mergeClasses(stackStyles.root, fieldGroupStyles.root)}>
            <div className={mergeClasses(stackStyles.item, checkboxStyles.root)}>
                <Field<boolean> name={AddOrUpdateExpirationFormFieldName.EnableExpiration}>
                    {(fieldProps) => {
                        const { input } = fieldProps;
                        const { onChange, value } = input;

                        return (
                            <AddOrUpdateExpirationFormEnableControl
                                expirationEnabled={value}
                                onChange={onChange}
                                isSubmitting={isSubmitting}
                            />
                        );
                    }}
                </Field>
            </div>
            <div className={stackStyles.item}>
                <Field<EnvironmentExpirationViewModel>
                    name={AddOrUpdateExpirationFormFieldName.EnvironmentExpiration}
                    isEqual={areExpirationDatesEqual}
                >
                    {(fieldProps) => {
                        const { input, meta } = fieldProps;
                        const { onChange, value: fieldValue } = input;
                        const { expirationCalendarDate, expirationTimeZone, expirationTime } = fieldValue;

                        const onExpirationTimeChange = React.useCallback(
                            (value) => {
                                onChange({
                                    ...fieldValue,
                                    expirationTime: value,
                                });
                            },
                            [onChange, fieldValue]
                        );

                        const onExpirationDateChange = React.useCallback(
                            (value) => {
                                onChange({
                                    ...fieldValue,
                                    expirationCalendarDate: value,
                                });
                            },
                            [onChange, fieldValue]
                        );

                        const onExpirationTimeZoneChange = React.useCallback(
                            (_event: SelectionEvents, data: OptionOnSelectData) => {
                                onChange({
                                    ...fieldValue,
                                    expirationTimeZone: data.optionValue,
                                });
                            },
                            [onChange, fieldValue]
                        );

                        return (
                            <>
                                {!!enableExpiration && (
                                    <div className={mergeClasses(stackStyles.root, timeFieldStyles.root)}>
                                        <div className={stackStyles.item}>
                                            <AddOrUpdateExpirationFormDateControlContainer
                                                value={expirationCalendarDate}
                                                onChange={onExpirationDateChange}
                                                isSubmitting={isSubmitting}
                                                selectedTimeZone={expirationTimeZone}
                                            />
                                        </div>
                                        <div className={stackStyles.item}>
                                            <AddOrUpdateExpirationFormTimeControlContainer
                                                value={expirationTime}
                                                onChange={onExpirationTimeChange}
                                                isSubmitting={isSubmitting}
                                            />
                                            <AddOrUpdateExpirationFormTimeZoneControl
                                                value={expirationTimeZone}
                                                onChange={onExpirationTimeZoneChange}
                                                isSubmitting={isSubmitting}
                                            />
                                            <FieldError meta={meta} />
                                        </div>
                                    </div>
                                )}
                            </>
                        );
                    }}
                </Field>
            </div>
        </div>
    );
};

export default AddOrUpdateExpirationFormFieldGroupProps;
